import { ACCEPTED_IMAGE_MIME_TYPES, ACCEPTED_IMAGE_TYPES, MAX_IMAGE_SIZE } from '@/constants'
import {
  numberSchema,
  stringSchema,
  unionSchema,
  urlSchema,
  objectSchema,
  arraySchema,
  enumSchema,
  emailWithEmptySchema,
  urlWithEmptySchema,
  createFileSchema,
  booleanSchema,
} from '@/schemas'
import { LeadTypeEnum } from '@/types'

export const clinicShortFormSchema = objectSchema({
  name: stringSchema,
  url: urlSchema,
  settings: objectSchema({
    property: objectSchema({
      timezone: stringSchema,
    }),
    contacts: objectSchema({
      address: stringSchema.optional(),
    }),
  }),
})

export const clinicFormSchema = objectSchema({
  name: stringSchema,
  url: urlSchema,
  exclude_navigations: arraySchema(stringSchema).optional(),
  website_logo: createFileSchema({
    maxSize: MAX_IMAGE_SIZE,
    acceptedMimeTypes: ACCEPTED_IMAGE_MIME_TYPES,
    acceptedTypesDescription: ACCEPTED_IMAGE_TYPES,
  }),
  social_review_urls: objectSchema({
    facebook: urlWithEmptySchema.optional(),
    google: urlWithEmptySchema.optional(),
    trustpilot: urlWithEmptySchema.optional(),
    yelp: urlWithEmptySchema.optional(),
  }),
  settings: objectSchema({
    property: objectSchema({
      timezone: stringSchema,
    }),
    color: objectSchema({
      primary: stringSchema.optional(),
    }),
    notifications: objectSchema({
      sender: objectSchema({
        email: emailWithEmptySchema.optional(),
      }),
      manager_autoresponder: objectSchema({
        disabled_lead_types: arraySchema(enumSchema(Object.values(LeadTypeEnum))).optional(),
      }).optional(),
    }),
    reminders: objectSchema({
      sms: objectSchema({
        unread_time_limit: unionSchema([numberSchema.min(0), stringSchema])
          .optional()
          .nullable(),
        read_check_delay: unionSchema([numberSchema.min(0), stringSchema])
          .optional()
          .nullable(),
      }).optional(),
    }),
    contacts: objectSchema({
      address: stringSchema.optional(),
      phone: stringSchema.optional(),
      google_maps_link: urlWithEmptySchema.optional(),
    }),
    social: objectSchema({
      facebook: urlWithEmptySchema.optional(),
      twitter: urlWithEmptySchema.optional(),
      'google-plus': urlWithEmptySchema.optional(),
      linkedin: urlWithEmptySchema.optional(),
      yelp: urlWithEmptySchema.optional(),
    }),
    filter: objectSchema({
      country: objectSchema({
        filterType: stringSchema.optional(),
        countryCodes: arraySchema(stringSchema).optional(),
      }),
    }),
    templates: objectSchema({
      autoresponder: objectSchema({
        sms: objectSchema({
          body: objectSchema({
            text: objectSchema({
              default: stringSchema.optional(),
            }),
          }),
          sms_inquiry: objectSchema({
            body: objectSchema({
              text: objectSchema({
                default: stringSchema.optional(),
              }),
            }),
          }),
        }),
        contact: objectSchema({
          header: objectSchema({
            text: objectSchema({
              default: stringSchema.optional(),
              order: stringSchema.optional(),
              calendar: stringSchema.optional(),
            }),
          }),
          footer: objectSchema({
            title: objectSchema({
              default: stringSchema.optional(),
              order: stringSchema.optional(),
              calendar: stringSchema.optional(),
            }),
            text: objectSchema({
              default: stringSchema.optional(),
              order: stringSchema.optional(),
              calendar: stringSchema.optional(),
            }),
          }),
          customLinks: objectSchema({
            default: arraySchema(
              objectSchema({
                text: stringSchema.optional().nullable(),
                url: stringSchema.optional().nullable(),
              })
            ),
            calendar: arraySchema(
              objectSchema({
                text: stringSchema.optional().nullable(),
                url: stringSchema.optional().nullable(),
              })
            ),
          }),
          contactPreferenceLinks: objectSchema({
            default: arraySchema(
              objectSchema({
                text: stringSchema.optional().nullable(),
              })
            ),
            calendar: arraySchema(
              objectSchema({
                text: stringSchema.optional().nullable(),
              })
            ),
          }),
        }),
        manager: objectSchema({
          header: objectSchema({
            text: objectSchema({
              default: stringSchema.optional(),
            }),
          }),
          footer: objectSchema({
            text: objectSchema({
              default: stringSchema.optional(),
            }),
          }),
        }),
      }),
    }),
    integrations: objectSchema({
      twilio: objectSchema({
        phone_number: stringSchema.optional().nullable(),
      }),
      calendly: objectSchema({
        access_token: stringSchema.optional().nullable(),
      }),
      remedly: objectSchema({
        client_id: stringSchema.optional().nullable(),
        client_secret: stringSchema.optional().nullable(),
      }),
    }),
    widget_settings: objectSchema({
      client_id: stringSchema.optional(),
      client_secret: stringSchema.optional(),
      allowed_domains: arraySchema(stringSchema).optional(),
      settings: objectSchema({
        primary_color: stringSchema.optional(),
        secondary_color: stringSchema.optional(),
        use_timezone: booleanSchema.optional(),
      }),
    }),
    ai_setting: objectSchema({
      system_prompt: stringSchema.optional(),
      user_prompt: stringSchema.optional(),
      api_key: stringSchema.optional(),
      type: stringSchema.optional(),
      temperature: unionSchema([numberSchema.min(0), stringSchema]).optional(),
    }),
    agent: objectSchema({
      enabled: booleanSchema.optional(),
      suggestions_enabled: booleanSchema.optional(),
      agent_name: stringSchema.optional().nullable(),
      agent_personality: stringSchema.optional().nullable(),
      special_instructions: stringSchema.optional().nullable(),
    }).optional(),
  }),
})
